import { cva, type VariantProps } from 'class-variance-authority'

/**
 * Button variants
 *
 * Design tokens from tailwind.config.js:
 * - main-color (#F7B01A) - Primary CTA background
 * - text-color (#1c1b21) - Hover text color
 * - custom-red (#d7263d) - Promo button background
 * - text-color (#1c1b21) - Newsletter button background
 * - main-color (#F7B01A) - Newsletter hover background
 * - font-main-font (Poppins) - Button font
 */
export const buttonVariants = cva(
  'inline-flex items-center justify-center gap-2 whitespace-nowrap rounded text-sm font-medium ' +
  'ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 ' +
  'focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 ' +
  '[&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0',
  {
    variants: {
      variant: {
        default: 'bg-primary text-primary-foreground hover:brightness-110',
        destructive: 'bg-destructive text-destructive-foreground hover:brightness-110',
        outline: 'border border-input bg-background hover:bg-accent hover:text-accent-foreground',
        secondary: 'bg-secondary text-secondary-foreground hover:brightness-110',
        ghost: 'hover:bg-accent hover:text-accent-foreground',
        link: 'text-primary underline-offset-4 hover:underline',
        // Add to Cart button - used in ProductCard and product pages
        // gap-0 overrides base gap-2
        // [&_svg]:size-6 for 24px cart icon
        addToCart: 'group/addtocart bg-main-color text-text-color font-main-font font-bold ' +
        'rounded gap-0 [&_svg]:size-6 ' +
        'focus:outline-none focus:bg-main-color active:bg-main-color ' +
        '!border !border-transparent hover:!border-text-color hover:!bg-main-color ' +
        'transition-all duration-300 ease-in-out',
        promo: 'bg-custom-red text-white font-bold hover:brightness-110',
        // PromoBlock button - BLACK on the orange band (bg-main-color band),
        // inverts to an outlined black-on-transparent chip on hover.
        promoBlock:
          'uppercase tracking-wider bg-text-color text-white font-main-font text-sm md:text-base font-bold ' +
          '!border !border-transparent hover:!border-text-color hover:!bg-transparent hover:!text-text-color ' +
          'focus:outline-none focus-visible:!border-text-color ' +
          'visited:text-white visited:no-underline ' +
          'transition-all duration-300 ease-in-out',
        newsletter: 'uppercase bg-text-color text-white font-bold hover:bg-main-color ' +
        'hover:text-text-color hover:-translate-y-0.5 ' +
        'active:translate-y-0 transition-all duration-300',
        // Scroll to top button - fixed position, circular
        scrollToTop: 'rounded-full bg-primary text-white ' +
        'hover:opacity-80 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 ' +
        'transition-opacity duration-200',
        // Wishlist button - icon-only, subtle design with heart icon
        // group/wishlist for hover state on children
        // text-gray-500 intentionally muted for idle state; hover upgrades to primary
        wishlist: 'group/wishlist bg-transparent text-gray-500 ' +
        'border border-gray-300 hover:border-primary hover:text-primary ' +
        'focus-visible:ring-primary/50 ' +
        'transition-all duration-300 ease-in-out [&_svg]:size-5',
        // Category card button - Black Friday category selection grid
        categoryCard:
          'rounded-xl bg-main-color text-text-color font-main-font text-base font-bold ' +
          'hover:scale-105 hover:text-white hover:shadow-first-btn-shadow ' +
          'transition-all duration-300 ease-in-out',
        // Expand/collapse button - same pattern as addToCart (bg-main-color, border on hover)
        expand: 'bg-main-color text-text-color font-main-font font-bold text-sm ' +
        '!border !border-transparent hover:!border-text-color hover:!bg-main-color ' +
        'focus:outline-none focus:bg-main-color active:bg-main-color ' +
        'transition-all duration-300 ease-in-out',
      },
      size: {
        default: 'px-5 py-3',
        sm: 'px-4 py-2',
        lg: 'px-9 py-4',
        icon: 'h-10 w-10',
        // Add to Cart button size - responsive heights for card context
        addToCart: 'h-[30px] md:h-[35px] xl:h-[45px] w-full px-4 py-1 md:w-[80%] md:max-w-[280px] md:px-6 md:py-2 xl:px-10 xl:p-5',
        // PromoBlock button size - larger padding to match addToCart feel
        promoBlock: 'px-8 py-4',
        // Category card button size - fills grid cell with padding
        categoryCard: 'w-11/12 p-5',
        full: 'w-full py-3',
        // Wishlist button size - square icon button, responsive
        wishlist: 'h-[30px] w-[30px] md:h-[35px] md:w-[35px] xl:h-[45px] xl:w-[45px] p-1.5 md:p-2 xl:p-2.5',
        // Scroll to top - fixed position with padding for icon
        scrollToTop: 'h-16 w-16 p-2 fixed bottom-[20px] left-[14px] z-50',
        // Expand button size
        expand: 'w-80 py-3 px-4 gap-3',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  }
)

export type ButtonVariants = VariantProps<typeof buttonVariants>
